import { useState } from 'react';
import {
  ArchiveIcon,
  BrainIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  CloudRainIcon,
  DownloadCloudIcon,
  FlameIcon,
  HandIcon,
  LeafIcon,
  LightbulbIcon,
  ListFilterIcon,
  MonitorIcon,
  MoonIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  SendIcon,
  Share2Icon,
  Trash2Icon,
} from 'lucide-react';
import {
  TL,
  TL_PATIENTEN,
  TL_ZORGVERLENER,
  TlAvatar,
  TlKaart,
  TlKnop,
  TlTabs,
  TlTerug,
  TlWrap,
} from './chrome.js';

/**
 * Patiëntenlijst en patiëntdetail van het nagebouwde Therapieland-portaal.
 *
 * Nagebouwd naar docs/designs/screenshots-therapieland/. Op die screenshots zijn
 * de echte patiëntgegevens weggelakt; overal staat hier fictieve demodata.
 */

type TlPatient = (typeof TL_PATIENTEN)[number];

/** Programma-iconen: kleuren gesampled uit patienten-overzicht-therapieland.png. */
const PROG_ROZE = '#e469a0';
const PROG_TEAL = '#2ca0b9';
const PROG_LEISTEEN = '#8896aa';
const PROG_ORANJE = '#e87426';
const PROG_KORAAL = '#e55c5e';
const PROG_PAARS = '#a38cb5';
const PROG_AMBER = '#f6a000';

interface TlProgramma {
  titel: string;
  kleur: string;
  Icon: typeof HandIcon;
  /** null betekent: nog niet gestart, dus geen voortgangsbalk. */
  voortgang: number | null;
  link: string;
}

const WERKMAPPEN: Record<string, TlProgramma[]> = {
  proef: [
    {
      titel: 'Opkomen voor jezelf',
      kleur: PROG_ROZE,
      Icon: HandIcon,
      voortgang: 9,
      link: 'Wat zijn jouw doelen?',
    },
    { titel: 'Accepteren', kleur: PROG_TEAL, Icon: CheckIcon, voortgang: null, link: '' },
    { titel: 'Mindfulness', kleur: PROG_LEISTEEN, Icon: BrainIcon, voortgang: null, link: '' },
    {
      titel: 'Ontspanning',
      kleur: PROG_ORANJE,
      Icon: LeafIcon,
      voortgang: 34,
      link: 'Bekijk de voortgang',
    },
  ],
  voorbeeld: [
    {
      titel: 'Piekeren',
      kleur: PROG_ORANJE,
      Icon: RefreshCwIcon,
      voortgang: 62,
      link: 'Bekijk de voortgang',
    },
    {
      titel: 'Ontspanning',
      kleur: PROG_ORANJE,
      Icon: LeafIcon,
      voortgang: 41,
      link: 'Wat zijn jouw doelen?',
    },
    { titel: 'Beter slapen', kleur: PROG_PAARS, Icon: MoonIcon, voortgang: null, link: '' },
    { titel: 'Somberheid', kleur: PROG_AMBER, Icon: CloudRainIcon, voortgang: null, link: '' },
  ],
  demo: [
    {
      titel: 'Omgaan met stress',
      kleur: PROG_KORAAL,
      Icon: FlameIcon,
      voortgang: 76,
      link: 'Bekijk de voortgang',
    },
    { titel: 'Mindfulness', kleur: PROG_LEISTEEN, Icon: BrainIcon, voortgang: null, link: '' },
    {
      titel: 'Accepteren',
      kleur: PROG_TEAL,
      Icon: CheckIcon,
      voortgang: 18,
      link: 'Wat zijn jouw doelen?',
    },
    { titel: 'Beter slapen', kleur: PROG_PAARS, Icon: MoonIcon, voortgang: null, link: '' },
  ],
  test: [
    {
      titel: 'Somberheid',
      kleur: PROG_AMBER,
      Icon: CloudRainIcon,
      voortgang: 27,
      link: 'Wat zijn jouw doelen?',
    },
    {
      titel: 'Opkomen voor jezelf',
      kleur: PROG_ROZE,
      Icon: HandIcon,
      voortgang: 55,
      link: 'Bekijk de voortgang',
    },
    { titel: 'Piekeren', kleur: PROG_ORANJE, Icon: RefreshCwIcon, voortgang: null, link: '' },
    { titel: 'Ontspanning', kleur: PROG_ORANJE, Icon: LeafIcon, voortgang: null, link: '' },
  ],
  kees: [
    {
      titel: 'Beter slapen',
      kleur: PROG_PAARS,
      Icon: MoonIcon,
      voortgang: 88,
      link: 'Bekijk de voortgang',
    },
    { titel: 'Omgaan met stress', kleur: PROG_KORAAL, Icon: FlameIcon, voortgang: null, link: '' },
    {
      titel: 'Mindfulness',
      kleur: PROG_LEISTEEN,
      Icon: BrainIcon,
      voortgang: 13,
      link: 'Wat zijn jouw doelen?',
    },
    { titel: 'Accepteren', kleur: PROG_TEAL, Icon: CheckIcon, voortgang: null, link: '' },
  ],
};

/** Leeftijd hoort bij de geboortedatum in TL_PATIENTEN; vast, zodat de demo niet verloopt. */
const LEEFTIJDEN: Record<string, number> = {
  proef: 64,
  voorbeeld: 47,
  demo: 35,
  test: 54,
  kees: 41,
};

function werkmapVan(id: string): TlProgramma[] {
  return WERKMAPPEN[id] ?? [];
}

export function TlPatienten({
  patientId,
  setPatientId,
  onProgramma,
}: {
  patientId: string | null;
  setPatientId: (id: string | null) => void;
  onProgramma: (titel: string) => void;
}): JSX.Element {
  const patient = TL_PATIENTEN.find((p) => p.id === patientId);

  if (!patient) {
    return <Lijst onKies={setPatientId} />;
  }
  return <Detail patient={patient} onTerug={() => setPatientId(null)} onProgramma={onProgramma} />;
}

type LijstTab = 'patienten' | 'niet-geaccepteerd' | 'archief' | 'gedeeld';

const LIJST_TABS: { key: LijstTab; label: string }[] = [
  { key: 'patienten', label: 'Patiënten(207)' },
  { key: 'niet-geaccepteerd', label: '(Nog) niet geaccepteerd(22)' },
  { key: 'archief', label: 'Archief(4)' },
  { key: 'gedeeld', label: 'Gedeeld(3)' },
];

function Lijst({ onKies }: { onKies: (id: string) => void }): JSX.Element {
  const [tab, setTab] = useState<LijstTab>('patienten');
  const [zoek, setZoek] = useState('');

  const term = zoek.trim().toLowerCase();
  const rijen = TL_PATIENTEN.filter(
    (p) => p.naam.toLowerCase().includes(term) || p.email.toLowerCase().includes(term),
  );

  return (
    <TlWrap>
      <h1 className="text-[30px] font-bold" style={{ color: TL.ink }}>
        Patiënten
      </h1>
      <div className="mb-7 mt-5">
        <TlKnop>
          <span className="flex items-center gap-2.5">
            <PlusIcon size={19} strokeWidth={2.4} /> Patiënt toevoegen
          </span>
        </TlKnop>
      </div>

      <TlTabs tabs={LIJST_TABS} actief={tab} onKies={setTab} />

      {tab !== 'patienten' ? (
        <p className="pt-8 text-[15.5px]" style={{ color: TL.muted }}>
          Dit tabblad is in deze demo niet uitgewerkt.
        </p>
      ) : (
        <>
          <Filters zoek={zoek} setZoek={setZoek} />
          <Tabel rijen={rijen} onKies={onKies} />
        </>
      )}
    </TlWrap>
  );
}

function Filters({ zoek, setZoek }: { zoek: string; setZoek: (v: string) => void }): JSX.Element {
  return (
    <>
      <div className="flex justify-end gap-5 pt-6">
        <div className="flex">
          <input
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            placeholder="Zoek op naam of e-mail"
            className="w-[240px] rounded-l px-4 py-2.5 text-[15px] outline-none"
            style={{ border: `1px solid ${TL.rand}`, color: TL.body }}
          />
          <span
            className="flex w-[52px] items-center justify-center rounded-r"
            style={{
              border: `1px solid ${TL.rand}`,
              borderLeft: 'none',
              backgroundColor: TL.chip,
            }}
          >
            <SearchIcon size={19} style={{ color: TL.body }} />
          </span>
        </div>
        <div
          className="flex w-[240px] items-center justify-between rounded px-4 py-2.5"
          style={{ border: `1px solid ${TL.rand}` }}
        >
          <span className="text-[15px]" style={{ color: TL.zacht }}>
            Zoek op geboortedatum
          </span>
          <CalendarIcon size={19} style={{ color: TL.primair }} />
        </div>
      </div>

      <div className="flex items-stretch gap-5 pt-5">
        <div
          className="flex flex-1 items-center gap-9 rounded px-5"
          style={{ border: `1px solid ${TL.rand}` }}
        >
          {['Alleen vragenlijsten', 'Recent ingelogd', 'Niet recent ingelogd'].map((label) => (
            <span key={label} className="flex items-center gap-3 py-3.5">
              <span
                className="h-[19px] w-[19px] rounded-[2px]"
                style={{ border: `2px solid ${TL.zacht}` }}
              />
              <span className="text-[15px]" style={{ color: TL.zacht }}>
                {label}
              </span>
            </span>
          ))}
        </div>
        <div
          className="flex w-[240px] items-center gap-3 rounded px-4"
          style={{ border: `1px solid ${TL.rand}` }}
        >
          <ListFilterIcon size={18} style={{ color: TL.body }} />
          <span className="flex-1 text-[15px]" style={{ color: TL.ink }}>
            Sorteer op Laatste actie
          </span>
          <ChevronDownIcon size={18} style={{ color: TL.ink }} />
        </div>
      </div>
    </>
  );
}

function Tabel({
  rijen,
  onKies,
}: {
  rijen: TlPatient[];
  onKies: (id: string) => void;
}): JSX.Element {
  return (
    <div className="mt-6 rounded" style={{ border: `1px solid ${TL.rand}` }}>
      <div
        className="grid items-center gap-4 px-6 py-3.5"
        style={{
          backgroundColor: TL.panel,
          gridTemplateColumns: '1.5fr 1.5fr 1fr 1.1fr 1fr',
        }}
      >
        {['Profiel', 'E-mail gebruikt voor uitnodiging', 'Geboortedatum', 'Werkmap'].map((kop) => (
          <span key={kop} className="text-[14.5px] font-semibold" style={{ color: TL.ink }}>
            {kop}
          </span>
        ))}
        <span className="text-right text-[14.5px] font-semibold" style={{ color: TL.ink }}>
          Nieuwe acties
        </span>
      </div>

      {rijen.length === 0 ? (
        <p className="px-6 py-6 text-[15px]" style={{ color: TL.muted }}>
          Geen patiënten gevonden.
        </p>
      ) : (
        rijen.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onKies(p.id)}
            className="grid w-full items-center gap-4 px-6 py-4 text-left"
            style={{
              borderTop: `1px solid ${TL.rand}`,
              gridTemplateColumns: '1.5fr 1.5fr 1fr 1.1fr 1fr',
            }}
          >
            <span className="flex items-center gap-4">
              <TlAvatar initialen={p.initialen} grootte={44} />
              <span className="text-[15.5px]" style={{ color: TL.ink }}>
                {p.naam}
              </span>
            </span>
            <span className="text-[15.5px]" style={{ color: TL.body }}>
              {p.email}
            </span>
            <span className="text-[15.5px]" style={{ color: TL.body }}>
              {p.gebdatum} {LEEFTIJDEN[p.id]}
            </span>
            <span className="flex items-center gap-2.5">
              {werkmapVan(p.id)
                .slice(0, 3)
                .map((prog) => (
                  <ProgrammaIcoon
                    key={prog.titel}
                    kleur={prog.kleur}
                    Icon={prog.Icon}
                    grootte={38}
                  />
                ))}
              <span
                className="flex h-[38px] w-[38px] items-center justify-center rounded-full text-[15px] font-semibold"
                style={{ backgroundColor: TL.chip, color: TL.muted }}
              >
                ...
              </span>
            </span>
            <span className="text-right text-[15px] underline" style={{ color: TL.primair }}>
              {p.nieuweActies} nieuwe {p.nieuweActies === 1 ? 'actie' : 'acties'}
            </span>
          </button>
        ))
      )}
    </div>
  );
}

function ProgrammaIcoon({
  kleur,
  Icon,
  grootte,
}: {
  kleur: string;
  Icon: typeof HandIcon;
  grootte: number;
}): JSX.Element {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ backgroundColor: kleur, width: grootte, height: grootte }}
    >
      <Icon size={grootte * 0.5} strokeWidth={2.2} className="text-white" />
    </span>
  );
}

type DetailTab = 'werkmap' | 'acties' | 'monitor';

const DETAIL_TABS: { key: DetailTab; label: string }[] = [
  { key: 'werkmap', label: 'Werkmap' },
  { key: 'acties', label: 'Acties' },
  { key: 'monitor', label: 'Monitor' },
];

function Detail({
  patient,
  onTerug,
  onProgramma,
}: {
  patient: TlPatient;
  onTerug: () => void;
  onProgramma: (titel: string) => void;
}): JSX.Element {
  const [tab, setTab] = useState<DetailTab>('werkmap');

  return (
    <TlWrap>
      <TlTerug label="cliënten" onClick={onTerug} />

      <DetailKop patient={patient} />

      <div className="pt-7">
        <TlTabs tabs={DETAIL_TABS} actief={tab} onKies={setTab} />
      </div>

      {tab === 'werkmap' && <TabWerkmap patient={patient} onProgramma={onProgramma} />}
      {tab === 'acties' && <TabActies patient={patient} />}
      {tab === 'monitor' && <TabMonitor />}
    </TlWrap>
  );
}

const KOP_ACTIES: { label: string; Icon: typeof SendIcon }[] = [
  { label: 'Stuur bericht', Icon: SendIcon },
  { label: 'Archiveren', Icon: ArchiveIcon },
  { label: 'Downloaden', Icon: DownloadCloudIcon },
  { label: 'Gegevens bewerken', Icon: PencilIcon },
];

function DetailKop({ patient }: { patient: TlPatient }): JSX.Element {
  return (
    <div className="mt-6 rounded px-8 py-7" style={{ backgroundColor: TL.panel }}>
      <div className="flex justify-between">
        <div className="flex gap-7">
          <TlAvatar initialen={patient.initialen} grootte={64} />
          <div>
            <h1 className="text-[26px] font-bold leading-tight" style={{ color: TL.ink }}>
              {patient.naam}
            </h1>
            <p className="pt-1.5 text-[15.5px]" style={{ color: TL.body }}>
              {patient.email}
            </p>
            <p className="pt-2 text-[15.5px]" style={{ color: TL.body }}>
              Geboortedatum: {patient.gebdatum}
            </p>
            <p className="pt-2 text-[15.5px]" style={{ color: TL.body }}>
              Geslacht: {patient.geslacht}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[15px]" style={{ color: TL.muted }}>
            Start datum: <span style={{ color: TL.body }}>{patient.startDatum}</span>
          </p>
          <p className="pt-3 text-[15px]" style={{ color: TL.muted }}>
            Laatste inlog: <span style={{ color: TL.body }}>{patient.laatsteInlog}</span>
          </p>
        </div>
      </div>

      <div className="flex gap-9 pl-[92px] pt-7">
        {KOP_ACTIES.map(({ label, Icon }) => (
          <span key={label} className="flex items-center gap-2.5">
            <Icon size={19} style={{ color: TL.primair }} />
            <span className="text-[15px] font-semibold" style={{ color: TL.primair }}>
              {label}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TabWerkmap({
  patient,
  onProgramma,
}: {
  patient: TlPatient;
  onProgramma: (titel: string) => void;
}): JSX.Element {
  return (
    <div className="flex gap-8 pt-8">
      <div className="min-w-0 flex-1">
        <h2 className="text-[21px] font-semibold" style={{ color: TL.ink }}>
          Werkmap
        </h2>
        <div className="flex flex-col gap-4 pt-5">
          {werkmapVan(patient.id).map((prog) => (
            <ProgrammaKaart key={prog.titel} prog={prog} onKies={() => onProgramma(prog.titel)} />
          ))}
        </div>
      </div>

      <ToegangKaart />
    </div>
  );
}

function ProgrammaKaart({ prog, onKies }: { prog: TlProgramma; onKies: () => void }): JSX.Element {
  return (
    <TlKaart>
      <button type="button" onClick={onKies} className="w-full px-6 py-5 text-left">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <ProgrammaIcoon kleur={prog.kleur} Icon={prog.Icon} grootte={36} />
            <span className="text-[16.5px] font-semibold" style={{ color: TL.ink }}>
              {prog.titel}
            </span>
          </div>
          <Trash2Icon size={19} style={{ color: TL.muted }} />
        </div>

        {prog.voortgang === null ? (
          <p className="pl-[52px] pt-3 text-[15.5px]" style={{ color: TL.body }}>
            Nog niet gestart
          </p>
        ) : (
          <div className="pl-[52px] pt-4">
            <span
              className="block h-[7px] w-full rounded-full"
              style={{ backgroundColor: TL.rand }}
            >
              <span
                className="block h-[7px] rounded-full"
                style={{ backgroundColor: TL.groen, width: `${prog.voortgang}%` }}
              />
            </span>
            <span className="mt-3 inline-block text-[15px] underline" style={{ color: TL.body }}>
              {prog.link}
            </span>
          </div>
        )}
      </button>
    </TlKaart>
  );
}

function ToegangKaart(): JSX.Element {
  return (
    <div className="w-[300px] shrink-0">
      <TlKaart>
        <div className="flex items-center justify-between px-6 py-5">
          <h2 className="text-[19px] font-semibold" style={{ color: TL.ink }}>
            Toegang
          </h2>
          <span
            className="flex items-center gap-2.5 rounded px-4 py-2.5 text-[14.5px] font-semibold"
            style={{ border: `1px solid ${TL.oranje}`, color: TL.oranje }}
          >
            <Share2Icon size={16} /> Uitnodigen
          </span>
        </div>

        <div className="flex gap-3 px-6" style={{ borderBottom: `1px solid ${TL.rand}` }}>
          <span
            className="rounded-t px-3 py-2 text-[14.5px] font-semibold"
            style={{ backgroundColor: TL.assistent, color: TL.primair }}
          >
            Toegang
          </span>
          <span className="px-3 py-2 text-[14.5px] font-semibold" style={{ color: TL.ink }}>
            Doel
          </span>
        </div>

        <div className="px-6 py-5">
          <p className="text-[15px]" style={{ color: TL.body }}>
            Volledige toegang
          </p>
          <span className="flex items-center gap-3.5 pt-4">
            <TlAvatar initialen={TL_ZORGVERLENER.initialen} grootte={34} />
            <span className="text-[15.5px]" style={{ color: TL.ink }}>
              {TL_ZORGVERLENER.naam}
            </span>
          </span>
        </div>
      </TlKaart>
    </div>
  );
}

function TabActies({ patient }: { patient: TlPatient }): JSX.Element {
  const eerste = werkmapVan(patient.id)[0];
  const vorige = [
    { tijd: '11:18', link: 'Van start! - Hoe zit het bij jou? Ingevuld' },
    { tijd: '10:39', link: 'In behandeling' },
  ];

  return (
    <div className="flex gap-8 pt-8">
      <div className="min-w-0 flex-1">
        <TlKaart>
          <div className="px-7 py-6">
            <h2 className="text-[21px] font-semibold" style={{ color: TL.ink }}>
              Nieuwe acties
            </h2>
            <p className="pt-4 text-[15.5px]" style={{ color: TL.body }}>
              Er zijn geen nieuwe acties.
            </p>

            <h2 className="pt-10 text-[21px] font-semibold" style={{ color: TL.ink }}>
              Vorige acties
            </h2>
            <div className="flex flex-col gap-4 pt-5">
              {vorige.map((actie) => (
                <span key={actie.tijd} className="flex items-center gap-6">
                  <span className="text-[15px]" style={{ color: TL.body }}>
                    {patient.laatsteInlog}
                  </span>
                  <span className="text-[15px]" style={{ color: TL.body }}>
                    {actie.tijd}
                  </span>
                  <ProgrammaIcoon kleur={TL.primair} Icon={eerste.Icon} grootte={26} />
                  <span className="text-[15px]" style={{ color: TL.body }}>
                    {eerste.titel} - <span className="underline">{actie.link}</span>
                  </span>
                </span>
              ))}
            </div>
          </div>
        </TlKaart>
      </div>

      <div className="w-[280px] shrink-0 rounded px-7 py-7" style={{ backgroundColor: TL.panel }}>
        <LightbulbIcon size={34} strokeWidth={1.6} style={{ color: TL.primair }} />
        <p className="pt-6 text-[15.5px] font-semibold" style={{ color: TL.ink }}>
          Nieuwe acties?
        </p>
        <p className="pt-1 text-[15.5px]" style={{ color: TL.zacht }}>
          Bekijk ze en geef feedback!
        </p>
        <p className="pt-5 text-[15.5px] font-semibold" style={{ color: TL.ink }}>
          Geen nieuwe acties?
        </p>
        <p className="pt-1 text-[15.5px]" style={{ color: TL.zacht }}>
          Motiveer met een bericht.
        </p>
      </div>
    </div>
  );
}

function TabMonitor(): JSX.Element {
  return (
    <div className="flex flex-col items-center pb-10 pt-14">
      <span
        className="flex h-[130px] w-[145px] items-center justify-center rounded"
        style={{ backgroundColor: TL.chip }}
      >
        <MonitorIcon size={86} strokeWidth={1.2} className="text-white" />
      </span>
      <p className="pt-9 text-[19px] font-bold" style={{ color: TL.ink }}>
        Je hebt geen toegang tot de monitor(s)
      </p>
      <p className="pt-4 text-[15.5px]" style={{ color: TL.body }}>
        Vraag toegang tot de monitor(s) van een cliënt
      </p>
      <div className="pt-6">
        <TlKnop>Vraag toegang</TlKnop>
      </div>
    </div>
  );
}
